import { graphql } from 'app/generated/gql'
import { useLocation, useSearchParams } from 'react-router-dom'
import { ColumnDef } from '@tanstack/react-table'
import {
  CalendarPlus as CalendarPlusIcon,
  ChatLines as ChatLinesIcon,
  EditPencil as EditPencilIcon,
  PageEdit as PageEditIcon,
  PhoneIncome as PhoneIncomeIcon,
  PhoneOutcome as PhoneOutcomeIcon,
  Reply as ReplyIcon,
} from 'iconoir-react'
import { clone, uniqBy, startCase } from 'lodash'
import { useState, useMemo, useEffect } from 'react'
import { ActivityDrawer } from '../activities/ActivityDrawer'
import { ContactDrawer } from '../contacts/ContactDrawer'
import { DateComponent } from '../DateComponent'
import PhoneNumber from '../PhoneNumber'
import { LazyLocationCard } from '../shared/LazyLocationCard'
import { ItemsTitle } from '../shared/styled/ItemsTitle'
import { AnchorCell } from '../table/AnchorCell'
import LinkedContactsCell from '../table/LinkedContactsCell'
import SimpleTable from '../table/SimpleTable'
import TagsCell from '../tags/TagsCell'
import type { ActivityJson, ActivityType, ContactJson } from 'app/types/entities'
import type { CallJson } from 'app/types/entities'
import { notifyError } from 'app/services/notify.client'
import { SearchResponse } from 'app/services/search'
import { useUser } from 'app/utils'
import { bg } from 'lib/background'
import { getChannelColorMap } from 'lib/colorMappings'
import { logError } from 'lib/logging'
import { Jsonify } from 'lib/toJSON'
import { useActivityStore, useContactStore } from 'app/stores/StoreContext'
import { gqlQuery } from 'app/lib/graphqlClient'

const STAFF_CALL_ACTIVITIES_QUERY = graphql(`
  query CallsModalStaffCallActivities(
    $dateRange: DateRangeInput!
    $bucket: String!
    $staffId: String
    $staffName: String
    $locationId: String
    $channel: String
    $locationIds: [String!]
    $filters: String
    $query: String
    $pagination: PaginationInput
  ) {
    staffCallActivities(
      dateRange: $dateRange
      bucket: $bucket
      staffId: $staffId
      staffName: $staffName
      locationId: $locationId
      channel: $channel
      locationIds: $locationIds
      filters: $filters
      query: $query
      pagination: $pagination
    ) {
      hits {
        id
        type
        subType
        activityDate
        contactName
        contacts {
          id
          name
          phone
        }
        locationId
        vendor
        phoneFromActivity
        channel
        utmSource
        utmMedium
        utmKeywords
        utmContent
        utmCampaign
        gclid
        referringUrl
        tags
        workspaceId
      }
      total
    }
  }
`)

const getActivityTypeIcon = (activityType?: string, subType?: string) => {
  let iconClasses = { className: `h-full w-full`, color: `#FFFFFF` }
  switch (activityType) {
    case `FollowUp`:
      return <ReplyIcon {...iconClasses} />
    case `Call`:
      return subType === `outbound` ? <PhoneOutcomeIcon {...iconClasses} /> : <PhoneIncomeIcon {...iconClasses} />
    case `Chat`:
      return <ChatLinesIcon {...iconClasses} />
    case `Form`:
      return <PageEditIcon {...iconClasses} />
    case `Scheduler`:
      return <CalendarPlusIcon {...iconClasses} />
    default:
      return <EditPencilIcon {...iconClasses} />
  }
}

export const defaultColumns = (
  activityHandler,
  isSuper,
  baseUrl?: string,
  onContactClick?: (contactId: string) => void,
): ColumnDef<ActivityType, any>[] => [
  {
    accessorFn: (row) => row.activityDate,
    id: `activityDate`,
    header: `Activity`,
    enableHiding: false,
    meta: {
      group: `General`,
      sortKey: `activityDate`,
      defaultVisible: true,
      pinned: true,
      ControlledComponent: (row) => (
        <AnchorCell
          href={`${baseUrl || ``}&drawer=${row?.id!}`}
          tooltipMessage={row?.type}
          clickHandler={() => activityHandler(row as ActivityJson)}
        >
          <div className="my-2 flex cursor-pointer items-center gap-x-2 align-middle">
            <div id="activity-record-icon" className="h-5 w-5 shrink-0 rounded-md bg-fill-neutral p-1">
              {getActivityTypeIcon(row?.type, row?.subType)}
            </div>
            <div className="flex flex-col justify-between overflow-hidden overflow-ellipsis whitespace-nowrap">
              <span className="text-ui-xs">
                {row?.type === `Scheduler`
                  ? `Online Scheduler`
                  : row?.type === `Call` && row?.subType
                    ? `${startCase(row.subType)} Call`
                    : startCase(row?.type)}
              </span>
              <DateComponent
                value={row?.activityDate}
                className="overflow-hidden overflow-ellipsis whitespace-nowrap text-body-xs text-subtle"
              />
            </div>
          </div>
        </AnchorCell>
      ),
    },
  },
  {
    accessorFn: (row) => row.contactName,
    id: `contactNames`,
    header: `Linked Contacts`,
    meta: {
      defaultVisible: true,
      group: `General`,
      sortKey: `contactName.keyword`,
      ControlledComponent: (row) => (
        <LinkedContactsCell contacts={row?.contacts as any} onContactClick={onContactClick} />
      ),
    },
  },
  {
    id: `tags`,
    accessorFn: (row) => row.tags,
    header: `Tags`,
    meta: {
      group: `General`,
      defaultVisible: true,
      ControlledComponent: (row) => (
        <TagsCell tags={row?.tags} workspaceId={row?.workspaceId} model="activities" modelId={row?.id!} />
      ),
    },
    enableSorting: false,
  },
  {
    accessorFn: (row) => row.locationId,
    header: `Location`,
    id: `locationId`,
    enableSorting: true,
    meta: {
      defaultVisible: true,
      group: `General`,
      sortKey: `locationId`,
      ControlledComponent: (row) => <LazyLocationCard locationId={row?.locationId} />,
    },
  },
  {
    accessorFn: (row) => row.vendor,
    header: `Vendor`,
    id: `vendor`,
    meta: {
      group: `General`,
      sortKey: `vendor.keyword`,
    },
  },
  {
    accessorFn: (row) => row.phoneFromActivity,
    header: `Phone Number`,
    id: `phoneFromActivity`,
    cell: (props) => <PhoneNumber number={props.getValue()} />,
    meta: {
      defaultVisible: true,
      group: `General`,
      sortKey: `phoneFromActivity.keyword`,
    },
  },
  {
    accessorFn: (row) => row.channel,
    header: `Channel`,
    id: `channel`,
    cell: (props) => (
      <>
        <span
          className="mr-1.5 inline-block h-2 w-2 rounded-[2px] align-middle"
          style={{ backgroundColor: getChannelColorMap(props.getValue()) }}
        ></span>
        <span>{props.row.original.channel}</span>
      </>
    ),
    meta: {
      defaultVisible: true,
      group: `Marketing`,
      sortKey: `channel.keyword`,
    },
  },
  {
    accessorFn: (row) => row.utmSource,
    header: `Source`,
    id: `utmSource`,
    meta: {
      defaultVisible: false,
      group: `Marketing`,
      sortKey: `utmSource.keyword`,
      tooltipClassName: `lowercase`,
    },
  },
  {
    accessorFn: (row) => row.utmMedium,
    header: `Medium`,
    id: `utmMedium`,
    meta: {
      defaultVisible: false,
      group: `Marketing`,
      sortKey: `utmMedium.keyword`,
      tooltipClassName: `lowercase`,
    },
  },
  {
    accessorFn: (row) => row.utmKeywords,
    header: `Keyword`,
    id: `utmKeywords`,
    meta: {
      defaultVisible: false,
      group: `Marketing`,
      sortKey: `utmKeywords.keyword`,
      tooltipClassName: `lowercase`,
    },
  },
  {
    accessorFn: (row) => row.utmContent,
    header: `Content`,
    id: `utmContent`,
    enableSorting: false,
    meta: {
      defaultVisible: false,
      group: `Marketing`,
      sortKey: `utmContent.keyword`,
      tooltipClassName: `lowercase`,
    },
  },
  {
    accessorFn: (row) => row.utmCampaign,
    header: `Campaign`,
    id: `utmCampaign`,
    meta: {
      defaultVisible: false,
      group: `Marketing`,
      sortKey: `utmCampaign.keyword`,
      tooltipClassName: `lowercase`,
    },
  },
  {
    accessorFn: (row) => row.gclid,
    header: `GCLID`,
    id: `gclid`,
    meta: {
      defaultVisible: false,
      group: `Marketing`,
      sortKey: `gclid.keyword`,
    },
  },
  {
    accessorFn: (row) => row.referringUrl,
    header: `Referral URL`,
    id: `referringUrl`,
    meta: {
      defaultVisible: false,
      group: `Marketing`,
      sortKey: `referringUrl.keyword`,
      tooltipClassName: `lowercase`,
    },
  },
]

export function CallsModal({
  fromDate,
  toDate,
  locationId,
  staffId,
  staffName,
  type,
  title,
  subtitle,
  onClose,
  channel,
  locationIds,
  filters,
}: {
  fromDate: string
  toDate: string
  type: `all` | `qualified-np` | `disqualified-np` | `scheduled-np` | `unscheduled-np`
  locationId?: string
  staffId?: string
  staffName?: string
  title: string
  subtitle?: string
  onClose: () => void
  channel?: string
  locationIds?: string[]
  filters?: string
}) {
  const [loadingCalls, setLoadingCalls] = useState(false)
  const [loadingDrawer, setLoadingDrawer] = useState(false)
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const baseUrl = `${location.pathname}${location.search.replace(/(\?|&)drawer=([\w-]+)/, ``)}`
  const user = useUser()
  const activityStore = useActivityStore()
  const contactStore = useContactStore()

  const [activityRows, setActivityRows] = useState<Jsonify<ActivityType>[]>()
  const [haveMore, setHaveMore] = useState(true)
  const [page, setPage] = useState(0)
  const [total, setTotal] = useState(0)
  const [focusedActivity, setFocusedActivity] = useState<ActivityJson>()
  const [openDrawer, setOpenDrawer] = useState(false)
  const [isActivityLoading, setIsActivityLoading] = useState(false)
  const [focusedContact, setFocusedContact] = useState<ContactJson>()
  const [openContactDrawer, setOpenContactDrawer] = useState(false)
  const [searchValue, setSearchValue] = useState<string>()

  const scrollHandler: React.UIEventHandler<HTMLDivElement> = (event: any) => {
    let element = event.target
    if (haveMore && !loadingCalls && Math.abs(element.scrollHeight - element.clientHeight - element.scrollTop) < 800) {
      setPage(page + 1)
    }
  }

  const onSearchValueChange = (searchValue: string) => {
    setSearchValue(searchValue)
    setHaveMore(true)
    setPage(0)
  }

  useEffect(() => {
    let activityId = searchParams.get(`drawer`)
    if (activityId && !focusedActivity) {
      setIsActivityLoading(true)
      bg(async () => {
        setLoadingDrawer(true)
        let activity: ActivityJson | undefined
        try {
          const result = await activityStore.fetchOne(activityId!)
          activity = (result.data as any)?.activity as ActivityJson | undefined
        } finally {
          setLoadingDrawer(false)
        }
        if (!activity) {
          const message = `not found`
          logError(`Error fetching activity`, message)
          notifyError(`Activity ${message}`)
          setFocusedActivity(undefined)
          setOpenDrawer(false)
        } else {
          setFocusedActivity(activity)
        }
        setIsActivityLoading(false)
      })
    }
  }, [])

  useEffect(() => {
    setOpenDrawer(!!focusedActivity)
  }, [focusedActivity])

  const activityHandler = (activity: ActivityJson) => {
    if (focusedActivity?.id === activity.id) return
    setFocusedContact(undefined)
    setOpenContactDrawer(false)
    setFocusedActivity(activity)
    let url = new URL(document.location.href)
    url.searchParams.set(`drawer`, activity.id!)
    history.replaceState({}, ``, url.toString())
  }

  const closeDrawerHandler = () => {
    setFocusedActivity(undefined)
    setOpenDrawer(false)
    let url = new URL(document.location.href)
    url.searchParams.delete(`drawer`)
    history.replaceState({}, ``, url.toString())
  }

  const contactHandler = (contactId: string) => {
    closeDrawerHandler()
    bg(async () => {
      const result = await contactStore.fetchOne(contactId)
      const contact = (result.data as any)?.contact as ContactJson | undefined
      if (!contact || result.error) {
        logError(`Error loading contact`, result.error?.message)
        notifyError(`Error loading contact`)
        return
      }
      setFocusedContact(contact)
      setOpenContactDrawer(true)
    })
  }

  const closeContactDrawerHandler = () => {
    setFocusedContact(undefined)
    setOpenContactDrawer(false)
  }

  useEffect(() => {
    if (haveMore && !loadingCalls) {
      bg(async () => {
        setLoadingCalls(true)
        let resp: { hits: Jsonify<ActivityType>[]; total: number } | undefined
        try {
          const result = await gqlQuery(STAFF_CALL_ACTIVITIES_QUERY, {
            dateRange: { fromDate, toDate },
            bucket: type,
            pagination: { page, limit: 25 },
            locationId: locationId || undefined,
            staffId: staffId || undefined,
            staffName: staffName || undefined,
            query: searchValue || undefined,
            channel: channel || undefined,
            locationIds: locationIds?.length ? locationIds : undefined,
            filters: filters || undefined,
          })
          // The table renders activity rows; the selection covers the fields it reads.
          resp = result.data?.staffCallActivities as typeof resp
        } finally {
          setLoadingCalls(false)
        }

        if (!resp?.hits?.length) {
          setHaveMore(false)
        } else {
          setHaveMore(true)
        }
        if (page === 0) {
          setTotal(resp?.total || 0)
        }
        setActivityRows((items) => {
          if (page === 0) {
            return resp?.hits || []
          } else {
            return clone(uniqBy((items || []).concat(resp?.hits || []), `id`))
          }
        })
      })
    }
  }, [haveMore, page, searchValue])

  const columns = useMemo(
    () => defaultColumns(activityHandler, user?.type === `super`, baseUrl, contactHandler),
    [activityHandler, baseUrl],
  )

  if (!user) return null

  return (
    <div x-component="CallsModal" className="flex h-[100vh] max-h-[100vh] flex-col overflow-hidden px-5">
      <div className="flex h-full w-full">
        <div className="flex h-full w-full flex-col overflow-hidden">
          <ItemsTitle title={title} subtitle={subtitle} items={total} onClose={onClose} />
          <SimpleTable
            data={activityRows}
            columns={columns as any}
            showColumnOrdering={true}
            onSearchValueChange={onSearchValueChange}
            onScroll={scrollHandler}
            hideFooter={true}
            meta={{ getRowStyle: (row) => (row?.id === focusedActivity?.id ? `table-selected-row` : ``) }}
          />
        </div>
        <ActivityDrawer
          activity={focusedActivity}
          isLoading={isActivityLoading}
          open={openDrawer}
          onClose={closeDrawerHandler}
          onContactClick={contactHandler}
        />
        <ContactDrawer
          contact={focusedContact}
          openDrawer={openContactDrawer}
          onClose={closeContactDrawerHandler}
          onChange={setFocusedContact}
        />
      </div>
    </div>
  )
}
